import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../../BasicComponents/Input/Input';
import { Button } from '../../BasicComponents/Button/Button';
import type { QuickAddScannerProps } from './types';

// ─── Keyboard-wedge barcode detector ─────────────────────────────────────────
// Hardware scanners type rapidly then press Enter. We detect this pattern by
// collecting chars with < 50ms gap, ending when Enter is pressed.

const WEDGE_INTERVAL_MS = 50;
/** Clear stale buffer chars after this inactivity period */
const BUFFER_TIMEOUT_MS = 500;

// ─── QuickAddScanner ──────────────────────────────────────────────────────────

export function QuickAddScanner({
  onScan,
  onManualAdd,
  onCameraToggle,
  isLoading = false,
  isCameraOpen = false,
  cameraSupported = true,
  placeholder = 'Scan barcode or search product\u2026',
  className,
  value,
  onValueChange,
}: QuickAddScannerProps) {
  const [ownQuery, setOwnQuery] = useState('');
  // Controlled when the page passes `value`; otherwise the box keeps its own text.
  const query = value ?? ownQuery;
  const setQuery = (next: string) => {
    if (value === undefined) setOwnQuery(next);
    onValueChange?.(next);
  };
  const [state, setState] = useState<'idle' | 'found' | 'not-found'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const bufferTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Auto-focus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Keyboard wedge detection on document level (works even if input not focused)
  useEffect(() => {
    function handleKeydown(e: KeyboardEvent) {
      // Keys typed into another field (e.g. the serial-capture input) belong to
      // that field, not to a product scan.
      const target = e.target as HTMLElement | null;
      if (
        target &&
        target !== inputRef.current &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }

      const now = Date.now();
      const gap = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      // Only Enter and character keys touch the buffer timeout. Other keys
      // (Backspace, arrows, Shift) leave it running so stale chars still clear.
      if (e.key === 'Enter') {
        if (bufferTimeoutRef.current) clearTimeout(bufferTimeoutRef.current);
        const barcode = bufferRef.current.trim();
        if (barcode.length > 2 && gap < WEDGE_INTERVAL_MS * 3) {
          e.preventDefault();
          onScan(barcode);
          setState('found');
          setTimeout(() => setState('idle'), 600);
        }
        // Enter always ends the buffer. A typed search + Enter must not leave
        // its letters behind for the next scan to append to.
        bufferRef.current = '';
        return;
      }

      if (e.key.length === 1) {
        if (bufferTimeoutRef.current) clearTimeout(bufferTimeoutRef.current);
        if (gap < WEDGE_INTERVAL_MS || bufferRef.current.length > 0) {
          bufferRef.current += e.key;
        } else {
          bufferRef.current = e.key;
        }

        // Auto-clear buffer after inactivity (prevents stale chars from slow typing)
        bufferTimeoutRef.current = setTimeout(() => {
          bufferRef.current = '';
        }, BUFFER_TIMEOUT_MS);
      }
    }

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      if (bufferTimeoutRef.current) clearTimeout(bufferTimeoutRef.current);
    };
  }, [onScan]);

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    // A keyboard-wedge scan into this input is handled by the document-level
    // listener above (onScan). React sees the Enter first, so step aside when
    // the keystrokes look like a scan — otherwise an exact-code manual add
    // would add the same item a second time.
    const looksLikeScan =
      bufferRef.current.trim().length > 2 &&
      Date.now() - lastKeyTimeRef.current < WEDGE_INTERVAL_MS * 3;
    if (looksLikeScan) return;
    const trimmed = query.trim();
    if (trimmed) {
      // Keep the typed text in the input so the user can clear it (and the
      // underlying search filter) via Backspace / select-all-delete. Wiping
      // the input here would desync the input value from the active filter
      // and leave the cashier with no way to restore the full grid. See #97.
      onManualAdd ? onManualAdd(trimmed) : onScan(trimmed);
    } else if (onManualAdd) {
      // Empty Enter clears any active search filter so the full grid returns.
      onManualAdd('');
    }
  }

  // Emptying the box by hand brings the full grid back, the same signal an
  // empty Enter sends. Text the page empties through `value` never comes
  // through here, so a page reset cannot loop back into a second fetch.
  function handleInputChange(next: string) {
    setQuery(next);
    if (query !== '' && next === '') onManualAdd?.('');
  }

  function handleClear() {
    setQuery('');
    onManualAdd?.('');
    inputRef.current?.focus();
  }

  return (
    <div
      className={[
        'flex items-center gap-2 transition-colors rounded-lg',
        state === 'found' ? 'ring-2 ring-success-500' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Barcode and QR scanner input"
    >
      <div className="flex-1 min-w-0">
        <Input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={isLoading}
          aria-label="Barcode and QR scanner input"
          prefix={
            <span className="pl-3 flex items-center shrink-0">
              <svg className="h-4 w-4 text-neutral-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 3h3v3H3zM3 10.5h3v3H3zM3 18h3v3H3zM10.5 3h3v3h-3zM18 3h3v3h-3zM10.5 10.5h3v3h-3zM18 10.5h3v3h-3zM10.5 18h3v3h-3zM18 18h3v3h-3z" />
              </svg>
            </span>
          }
          suffix={
            query ? (
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear search"
                className="pr-3 flex items-center shrink-0 text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            ) : null
          }
        />
      </div>
      {onCameraToggle && (
        <Button
          variant="secondary"
          size="md"
          onClick={onCameraToggle}
          disabled={!cameraSupported}
          aria-label="Scan barcode with camera"
          className={isCameraOpen ? 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-900/20' : ''}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        </Button>
      )}
    </div>
  );
}
